'use client';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { api } from '@/trpc/react';
import {
  AspectRatio,
  convertToReadableText,
  DEFAULT_INITIAL_GENERATION_WORKFLOW_PIPELINE,
  ALLOWED_PIPELINES
} from '@acme/lib';
import MultiLevelDialog from './Dialog/multi-level-dialog';
import { WildcardCategoryStep } from './photo-pack-details/WildcardCategoryStep';
import { WildcardItemSelectorStep } from './photo-pack-details/WildcardItemSelectorStep';
import { ClothingColorSelectorStep } from './photo-pack-details/ClothingColorSelectorStep';

import { GlassesWildcardItemSelectorStep } from './photo-pack-details/GlassesWildcardItemSelectorStep';
import '@acme/db/types';
import { isOptionalWildcard } from '@acme/lib';
import { UploadClothingStep } from './photo-pack-details/UploadClothingStep';
import { PhotoPackOptionsForm } from './photo-pack-details/PhotoPackOptionsForm';
import { ModelTrainingState, ThemeConfiguration, useTrainingAtom } from '@/atoms/useTrainingAtom';
import { CartState } from '@/atoms/useCartAtom';
import { useMemo } from 'react';
import usePhotoPackAction from '@/atoms/usePhotoPackAction';

export const clothingModifiers = {
  Random: 'Random',
  'T-Shirt': 'T-Shirt',
  Hoodie: 'Hoodie',
  Sweatshirt: 'Sweatshirt'
};

export function PhotoPackDetails({
  open,
  onClose,
  initialValues,
  onSubmit,
  themeId,
  onRemove,
  modelVersion,
  currentAmountOfPacksAllowed,
  buttonLabel,
  modelSelectorMode,
  modelLocked
}: {
  open: boolean;
  onClose: () => void;
  initialValues: ThemeConfiguration;
  onSubmit: (data: any) => void;
  themeId: string;
  onRemove: () => void;
  modelVersion?: string;
  currentAmountOfPacksAllowed?: number;
  buttonLabel?: string;
  modelSelectorMode?: 'training' | 'selection';
  modelLocked?: boolean;
}) {
  const [currentStepId, setCurrentStepId] = React.useState('main');
  const [wildcardModifierKey, setWildcardModifierKey] = React.useState<string | null>(null);
  const [currentCategory, setCurrentCategory] = React.useState<string | null>(null);
  const [selectedModel, setSelectedModel] = React.useState<ModelTrainingState | CartState['model'] | null>(null);
  const { training } = useTrainingAtom();

  const { model } = usePhotoPackAction({
    mode: modelSelectorMode === 'training' ? 'training' : 'cart'
  });

  const [selectedWildcardItem, setSelectedWildcardItem] = React.useState<string | undefined>(undefined);
  const userModelsQuery = api.model.getUserModels.useInfiniteQuery(
    {},
    {
      getNextPageParam: lastPage => lastPage.nextCursor,
      enabled: modelSelectorMode === 'selection'
    }
  );

  // Fetch the user's preferred model
  const preferredModelQuery = api.modelRepository.getPreferredModel.useQuery(undefined, {
    enabled: modelSelectorMode === 'selection'
  });

  // Flatten models data for easier access
  const models = React.useMemo(() => {
    return userModelsQuery.data?.pages.flatMap(page => page.models) || [];
  }, [userModelsQuery.data?.pages]);

  React.useEffect(() => {
    console.log('modelSelectorMode', modelSelectorMode);
    // In training mode, use the training model
    if (modelSelectorMode === 'training' && selectedModel === null) {
      setSelectedModel(training);
    }
    // In selection mode, use the preferred model when models load
    if (modelSelectorMode === 'selection' && selectedModel === null) {
      if (model) {
        setSelectedModel(model as CartState['model']);
      } else if (preferredModelQuery.data?.model?.defaultVersion && models.length > 0) {
        const preferredModel = preferredModelQuery.data.model.defaultVersion;
        setSelectedModel(preferredModel as unknown as CartState['model']);
      } else if (models.length > 0) {
        // Fall back to the first model if no preferred model is available
        setSelectedModel(models[0] as CartState['model']);
      }
    }
  }, [modelSelectorMode, training, models, selectedModel, model, preferredModelQuery.data]);

  const gender = useMemo(() => {
    if (selectedModel && 'attributes' in selectedModel) {
      return selectedModel.attributes?.gender as 'male' | 'female';
    }
    return undefined;
  }, [selectedModel]);

  const themeQuery = api.theme.getThemeWithWildcards.useQuery({
    themeId: themeId!,
    gender: gender ? [gender] : undefined
  });

  const getDefaultModifiers = () => {
    const defaultModifiers: Record<string, string | undefined> = {
      glasses: undefined
    };

    if (themeQuery.data?.modifiers) {
      Object.entries(themeQuery.data.modifiers).forEach(([category, _]) => {
        if (isOptionalWildcard(category)) {
          defaultModifiers[category] = 'None';
        }
      });
    }

    const mergedModifiers = {
      ...defaultModifiers,
      ...initialValues?.selectedModifiers
    };

    return mergedModifiers;
  };

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      ...initialValues,
      quantity: initialValues?.quantity || 1,
      photoQuantity: initialValues?.photoQuantity || 40,
      selectedModifiers: getDefaultModifiers(),
      garmentImages: initialValues?.garmentImages || []
    }
  });

  const onSubmitForm = (data: any) => {
    onSubmit({
      ...data,
      id: themeId!,
      name: themeQuery.data?.theme.name || data.name || 'Pack Name',
      image: themeQuery.data?.theme.image || data.image || '/images/pack-covers/blank.jpg',
      quantity: Number(data.quantity),
      workflowName: data.workflowName || DEFAULT_INITIAL_GENERATION_WORKFLOW_PIPELINE,
      model: selectedModel,
      slug: themeQuery.data?.theme.slug
    });
    onClose();
  };

  const aspectRatio = watch('photoSize.aspectRatio');

  React.useEffect(() => {
    if (themeQuery.data?.theme.meta && typeof themeQuery.data.theme.meta === 'object') {
      const meta = themeQuery.data.theme.meta as {
        defaultAspectRatio?: string;
        defaultWorkflow?: string;
      };

      // Set default aspect ratio if available
      if (meta.defaultAspectRatio && !aspectRatio && aspectRatio !== meta.defaultAspectRatio) {
        setValue('photoSize.aspectRatio', meta.defaultAspectRatio as AspectRatio);
      }

      // Set default workflow if available
      if (meta.defaultWorkflow && ALLOWED_PIPELINES.includes(meta.defaultWorkflow)) {
        console.log('Setting default workflow:', meta.defaultWorkflow);
        setValue('workflowName', meta.defaultWorkflow);
      }
    }
  }, [themeQuery.data]);

  const Title = initialValues?.name || 'Pack Name';
  const currentQuantity = initialValues?.quantity;
  const isUpdating = currentQuantity && currentQuantity > 0;

  const handleStepChange = React.useCallback((stepId: string) => {
    setCurrentStepId(stepId);
  }, []);

  console.log('selectedModelParent', selectedModel);

  const steps = React.useMemo(
    () => [
      {
        id: 'main',
        title: 'Photo Pack Details',
        component: ({ onGoNext }: { onGoNext: (nextStepId?: string) => void }) => (
          <PhotoPackOptionsForm
            modelLocked={modelLocked}
            modelSelectorMode={modelSelectorMode}
            selectedModel={selectedModel}
            onModelChange={model => {
              if (model) {
                setSelectedModel({
                  ...model
                });
              }
            }}
            Title={Title}
            themeQuery={themeQuery}
            control={control}
            watch={watch}
            gender={gender}
            themeId={themeId}
            modelVersion={modelVersion}
            currentAmountOfPacksAllowed={currentAmountOfPacksAllowed}
            currentQuantity={currentQuantity || 0}
            onSubmit={handleSubmit(onSubmitForm)}
            onGoNext={data => {
              if (data.category === 'glasses') {
                setCurrentCategory('glasses');
                setCurrentStepId('glasses');
                setWildcardModifierKey('casual');
                onGoNext('glasses');
              } else if (data.category === 'garment') {
                setCurrentCategory('garment');
                setCurrentStepId('garment');
                onGoNext('garment');
              } else if (data.wildcardCategory) {
                setCurrentCategory(data.category);
                setCurrentStepId('wildcard-category');
                onGoNext('wildcard-category');
              } else if (data.wildcardModifierKey) {
                setWildcardModifierKey(data.wildcardModifierKey);
                setCurrentCategory(data.category);
                setCurrentStepId('wildcard-item-selector');
                onGoNext('wildcard-item-selector');
              }
            }}
          />
        )
      },
      {
        id: 'glasses',
        title: 'Glasses',
        component: () => (
          <GlassesWildcardItemSelectorStep
            themeId={themeId}
            gender={gender}
            wildcardModifierKey={wildcardModifierKey!}
            category={currentCategory!}
            control={control}
            setValue={setValue}
            showModelGlasses={false}
            trainingImages={[]}
          />
        )
      },
      {
        id: 'garment',
        title: 'Upload Custom Clothing',
        component: () => <UploadClothingStep themeId={themeId} gender={gender} control={control} setValue={setValue} />
      },
      {
        id: 'wildcard-category',
        title: 'Select Category',
        component: ({ onGoNext }: { onGoNext: (nextStepId?: string) => void }) => (
          <WildcardCategoryStep
            category={currentCategory!}
            gender={gender}
            control={control}
            selectedValue={wildcardModifierKey || undefined}
            onSelect={wildcardModifierKey => {
              setWildcardModifierKey(wildcardModifierKey);
              setCurrentStepId('wildcard-item-selector');
              onGoNext('wildcard-item-selector');
            }}
          />
        )
      },
      {
        id: 'wildcard-item-selector',
        title: wildcardModifierKey ? convertToReadableText(wildcardModifierKey) : 'Category',
        component: ({ onGoNext }: { onGoNext: (nextStepId?: string) => void }) => (
          <WildcardItemSelectorStep
            themeId={themeId}
            gender={gender}
            wildcardModifierKey={wildcardModifierKey!}
            category={currentCategory!}
            control={control}
            setValue={setValue}
            onColorSelect={selectedItem => {
              setSelectedWildcardItem(selectedItem);
              setCurrentStepId('clothing-color-selector');
              onGoNext('clothing-color-selector');
            }}
          />
        )
      },
      {
        id: 'clothing-color-selector',
        title: 'Select Item Color',
        component: () => (
          <ClothingColorSelectorStep
            control={control}
            setValue={setValue}
            category={currentCategory!}
            wildcardModifierKey={wildcardModifierKey!}
            previousValue={selectedWildcardItem}
            themeId={themeId}
            gender={gender}
          />
        )
      }
    ],
    [themeId, gender, wildcardModifierKey, currentCategory, control, setValue, themeQuery, selectedWildcardItem]
  );

  const footerContent = React.useCallback(
    ({ onGoBack }: { onGoBack: (stepId?: string) => void }) => {
      if (currentStepId === 'main') {
        return (
          <>
            <Button size="lg" variant="outline" onClick={onClose} className="md:mr-auto">
              Cancel
            </Button>
            {isUpdating && (
              <Button type="button" variant="secondary" onClick={onRemove} className="w-full md:w-fit" size="lg">
                Remove
              </Button>
            )}
            <Button type="submit" form="updatePhotoPackForm" className="w-full md:w-fit" variant={'accent'} size={'lg'}>
              {buttonLabel || (isUpdating ? 'Update' : 'Add')}
            </Button>
          </>
        );
      } else if (
        currentStepId === 'wildcard-item-selector' ||
        currentStepId === 'glasses' ||
        currentStepId === 'garment'
      ) {
        return (
          <Button variant="default" className="w-full mt-4" onClick={() => onGoBack('main')}>
            Done
          </Button>
        );
      } else if (currentStepId === 'clothing-color-selector') {
        return (
          <Button variant="default" className="w-full mt-4" onClick={() => onGoBack('main')}>
            Done
          </Button>
        );
      }
      return null;
    },
    [currentStepId, isUpdating, onClose, onRemove, buttonLabel]
  );

  React.useEffect(() => {
    if (open) {
      setCurrentStepId('main');
    }
  }, [open]);

  return (
    <MultiLevelDialog
      steps={steps}
      isOpen={open}
      onClose={onClose}
      footer={footerContent}
      currentStepId={currentStepId}
      onStepChange={handleStepChange}
    />
  );
}
